import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Star, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY, LOCATIONS } from "../content";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#F2E9D8] border-t border-[#6E1F2C]/20 mt-32"
    >
      {/* Top burgundy accent bar */}
      <div className="h-[3px] w-full bg-gradient-to-r from-[#6E1F2C] via-[#B8904E] to-[#6E1F2C]" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-6">Est. 1993 · UAE & India</p>
            <h2 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-[#1C110C]">
              Let's shape
              <br />
              your <em className="text-[#6E1F2C] not-italic">space</em>.
            </h2>
            <Link
              to="/contact"
              data-testid="footer-cta-begin"
              className="mt-10 inline-flex items-center gap-4 border-b border-[#6E1F2C] pb-2 text-[12px] tracking-[0.28em] uppercase text-[#6E1F2C] hover:gap-6 transition-all"
            >
              Begin a Project <span>→</span>
            </Link>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow mb-5">Contact</p>
            <ul className="space-y-3 text-sm text-[#3E2A22]">
              <li className="flex gap-3">
                <Mail size={14} className="mt-1 text-[#6E1F2C]" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email"
                  className="link-elegant hover:text-[#6E1F2C]"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Phone size={14} className="mt-1 text-[#6E1F2C]" />
                <a
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="link-elegant hover:text-[#6E1F2C]"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <MapPin size={14} className="mt-1 text-[#6E1F2C]" />
                <span>Dubai · Sharjah · Al Sajja</span>
              </li>
            </ul>
            <div className="mt-6 flex gap-3">
              <a
                href={`https://instagram.com/${COMPANY.instagram}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 border border-[#6E1F2C]/40 text-[#6E1F2C] inline-flex items-center justify-center hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-all"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={`https://facebook.com/${COMPANY.facebook}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 border border-[#6E1F2C]/40 text-[#6E1F2C] inline-flex items-center justify-center hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-all"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://maps.google.com/maps?cid=17721573559901945835"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-google-review"
                className="w-10 h-10 border border-[#D4A963] text-[#D4A963] inline-flex items-center justify-center hover:bg-[#D4A963] hover:text-[#1C110C] transition-all"
                aria-label="Read our Google reviews"
                title="Read our Google reviews"
              >
                <Star size={16} fill="currentColor" />
              </a>
            </div>
            <a
              href="https://maps.google.com/maps?cid=17721573559901945835"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-google-review-text"
              className="mt-3 inline-block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C] transition-colors"
            >
              ★ 4.3 / 5 · Read our Google reviews
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow mb-5">Explore</p>
            <ul className="space-y-3 text-sm text-[#3E2A22]">
              <li>
                <Link to="/services" data-testid="footer-link-services" className="link-elegant hover:text-[#6E1F2C]">Services</Link>
              </li>
              <li>
                <Link to="/kitchen-renovation-dubai" data-testid="footer-link-kitchen" className="link-elegant hover:text-[#6E1F2C]">Kitchen Renovation</Link>
              </li>
              <li>
                <Link to="/custom-wardrobes-dubai" data-testid="footer-link-wardrobes" className="link-elegant hover:text-[#6E1F2C]">Custom Wardrobes</Link>
              </li>
              <li>
                <Link to="/custom-joinery-dubai" data-testid="footer-link-joinery" className="link-elegant hover:text-[#6E1F2C]">Custom Joinery</Link>
              </li>
              <li>
                <Link to="/custom-sofas-dubai" data-testid="footer-link-sofas" className="link-elegant hover:text-[#6E1F2C]">Custom Sofas</Link>
              </li>
              <li>
                <Link to="/portfolio" data-testid="footer-link-portfolio" className="link-elegant hover:text-[#6E1F2C]">Portfolio</Link>
              </li>
              <li>
                <Link to="/ongoing-projects" data-testid="footer-link-ongoing" className="link-elegant hover:text-[#6E1F2C]">Ongoing Projects</Link>
              </li>
              <li>
                <Link to="/curtains-dubai" data-testid="footer-link-curtains" className="link-elegant hover:text-[#6E1F2C]">Curtains &amp; Blinds</Link>
              </li>
              <li>
                <Link to="/holiday-homes" data-testid="footer-link-holiday" className="link-elegant hover:text-[#6E1F2C]">Holiday Homes</Link>
              </li>
              <li>
                <Link to="/blog" data-testid="footer-link-blog" className="link-elegant hover:text-[#6E1F2C]">Blog</Link>
              </li>
              <li>
                <Link to="/fit-out-cost-calculator-dubai" data-testid="footer-link-calculator" className="link-elegant hover:text-[#6E1F2C]">Budget Planner</Link>
              </li>
            </ul>
            <p className="eyebrow mb-5 mt-10">Studios</p>
            <ul className="space-y-5 text-sm text-[#7A6B5F]">
              {LOCATIONS.map((l) => (
                <li key={l.name}>
                  <p className="text-[#1C110C] text-[13px]">{l.city}</p>
                  <p className="text-xs mt-1 leading-relaxed">{l.address}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-[#6E1F2C]/20 mt-20 pt-10">
          <p
            data-testid="footer-entity-tagline"
            className="font-serif-display text-[15px] md:text-base text-[#1C110C] leading-relaxed"
          >
            <strong className="font-medium">
              Time Furniture Turnkey Projects Contracting LLC
            </strong>
            {" — "}an interior fit-out contractor and custom furniture manufacturer
            serving Dubai, Abu Dhabi, Sharjah, and the wider UAE since 1993.
          </p>

          <div className="mt-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#6B5E55]">
            <div className="space-y-1.5">
              <p data-testid="footer-founder" className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">
                Founded by Vijayan Sahadevan · 1993
              </p>
              <p data-testid="footer-copyright">
                © {new Date().getFullYear()} Time Furniture · UAE grown business since 1993 · All rights reserved.
              </p>
            </div>
            <div className="flex items-center gap-5">
              <Link
                to="/projects"
                data-testid="footer-portal-link"
                className="hover:text-[#6E1F2C] tracking-[0.28em] uppercase text-[10px]"
              >
                Client Portal
              </Link>
              <span className="text-[#6E1F2C]/30 text-[10px]" aria-hidden="true">|</span>
              <Link
                to="/admin/login"
                data-testid="footer-admin-link"
                className="hover:text-[#6E1F2C] tracking-[0.28em] uppercase text-[10px]"
              >
                Admin Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
